import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, takeUntil, take, filter, Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  PanelBodyComponent,
  PanelContainerComponent,
} from 'src/app/shared/content-panels';
import { NavService } from 'src/app/shared/navigation';
import { QBuiltService } from '../services/qbuilt-service';
import { Zone } from '../models/qbuilt-admin.models';
import { localStorageConstants } from 'src/app/shared/services';
import { HbgSnackBar } from 'src/app/shared/toaster/hbg-toaster';
import { CanCQMSComponentDeactivate } from 'src/app/shared/guards/cqms-pending-changes.guard';
import { QBuiltConstants } from '../models/constants/qbuilt.constants';

interface UserInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  plantNumber: string;
  plantName: string;
}

@Component({
    selector: 'qbuilt-user-edit',
    imports: [
    MatButtonModule,
    MatIconModule,
    PanelContainerComponent,
    PanelBodyComponent
],
    templateUrl: './user-edit.component.html',
    styleUrl: './user-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserEditComponent implements OnInit, OnDestroy, CanCQMSComponentDeactivate {
  user: UserInfo | null = null;
  assignedZones: Zone[] = [];
  unassignedZones: Zone[] = [];

  isLoading = true;
  isSaving = false;
  saveError: string | null = null;

  private initialAssignedZoneIds = '';

  changesMade: boolean = false; // Flag to track if changes have been made
  saveTrigger: boolean = false; // Flag to indicate if save is in progress

  get hasFormChanged(): boolean {
    const current = [...this.assignedZones].map((z) => z.id).sort().join(',');
    //checks for difference in zones if true then triggers save changes modal
    if(current !== this.initialAssignedZoneIds) {
      this.changesMade = true;
    }
    return current !== this.initialAssignedZoneIds;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private navService: NavService,
    private qbuiltService: QBuiltService,
    private cdr: ChangeDetectorRef,
    private toaster: HbgSnackBar,
    private qbuiltConstants: QBuiltConstants
  ) {}

  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
  
  ngOnInit(): void {
    this.navService.setActive('CQMS');
    const userId = Number(this.route.snapshot.paramMap.get('userId'));
    this.qbuiltService.setFacilityNumber(localStorageConstants.USER_FACILITIES_KEY);
    this.qbuiltService.facilityNumber$
      .pipe(filter(Boolean), take(1))
      .subscribe((plantNumber) => {
        this.loadUserData(userId, plantNumber);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  moveToAssigned(zone: Zone): void {
    this.unassignedZones = this.unassignedZones.filter((z) => z.id !== zone.id);
    this.assignedZones = [...this.assignedZones, zone].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.saveError = null;
  }

  moveToUnassigned(zone: Zone): void {
    this.assignedZones = this.assignedZones.filter((z) => z.id !== zone.id);
    this.unassignedZones = [...this.unassignedZones, zone].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.saveError = null;
  }

  // Implementation for CanComponentDeactivate interface
  hasUnsavedChanges(): boolean {
    return this.changesMade;
  }

  getSaveChangesDialogConfig() {
    return {
      title: this.qbuiltConstants.dialogTitle,
      message: this.qbuiltConstants.dialogMessage,
      cancelButtonText: this.qbuiltConstants.cancelButtonText,
      confirmButtonText: this.qbuiltConstants.confirmButtonText
    };
  }

  isSaveInProgress(): boolean {
    return this.saveTrigger;
  }

  save(): void {
    if (!this.user) return;
    this.saveTrigger = true;
    this.isSaving = true;
    this.saveError = null;
    const zoneIds = this.assignedZones.map((z) => Number(z.id));
    const zoneIdsToRemove = this.initialAssignedZoneIds
      .split(',')
      .filter(Boolean)
      .map(Number)
      .filter((id) => !zoneIds.includes(id));

    this.qbuiltService
      .updateUserZoneAssignments(Number(this.user.id), zoneIds, zoneIdsToRemove)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isSaving = false;
          if (result.Success) {
            this.toaster.open('Zone assignments saved successfully!');
            this.router.navigate(['/cqms/users']);
          } else {
            this.saveError = result.Message;
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          this.isSaving = false;
          this.saveError = 'Failed to save zone assignments.';
          this.toaster.stickyError('Failed to save zone assignments.');
          console.error(err);
          this.cdr.markForCheck();
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/cqms/users']);
  }

  private loadUserData(userId: number, plantNumber: string): void {
    forkJoin({
      userDetail: this.qbuiltService.getUserZonesById(userId, plantNumber),
      allFacilityZones: this.qbuiltService.getZonesByFacility(plantNumber),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ userDetail, allFacilityZones }) => {
          this.user = {
            id: String(userDetail.UserId),
            firstName: userDetail.FirstName,
            lastName: userDetail.LastName,
            email: userDetail.Email,
            phoneNumber: userDetail.PhoneNumber ?? '',
            plantNumber: userDetail.PlantNumber ?? '',
            plantName: userDetail.PlantName ?? '',
          };

          // Build the set of currently assigned zone IDs
          const assignedIds = new Set<string>(
            (userDetail.Zones ?? []).map((z: any) => String(z.ZoneId))
          );

          this.assignedZones = (userDetail.Zones ?? [])
            .map((z: any) => ({
              id: String(z.ZoneId),
              name: z.ZoneName,
              facilityId: '',
              facilityName: userDetail.PlantName ?? '',
            }))
            .sort((a: Zone, b: Zone) => a.name.localeCompare(b.name));

          this.initialAssignedZoneIds = [...this.assignedZones].map((z) => z.id).sort().join(',');

          // All zones for this facility directly from quality.zones
          this.unassignedZones = (allFacilityZones ?? [])
            .filter((z: any) => !assignedIds.has(String(z.Id)))
            .map((z: any) => ({
              id: String(z.Id),
              name: z.Name,
              facilityId: z.HBGFacilityNumber ?? '',
              facilityName: userDetail.PlantName ?? '',
            }))
            .sort((a: Zone, b: Zone) => a.name.localeCompare(b.name));

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load user data', err);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
